// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { LogManager } from "@lib/logging/LogManager";
import { ILocalRpcHub } from "@lib/messaging/rpc/ILocalRpcHub";
import { IRpcContext } from "@lib/messaging/rpc/IRpcContext";
import { JsonRpcErrorCode } from "@lib/messaging/rpc/jsonRpc/JsonRpcErrorCode";
import { RpcCallback } from "@lib/messaging/rpc/RpcCallback";
import { RpcError } from "@lib/messaging/rpc/RpcError";
import { RpcResult } from "@lib/messaging/rpc/RpcResult";
import { ArgumentError } from "@lib/utilities/ArgumentError";
import { Err, Ok } from "@lib/utilities/Result";

function createRpcCallError(method: string, value: any): RpcError<any>
{
  if (value instanceof RpcError)
    return value;

  if (value instanceof ArgumentError)
  {
    log.error("Execution of method '%s' failed: Invalid parameters (%o)", method, value);
    return RpcError.createJsonRpc(JsonRpcErrorCode.InvalidParameters);
  }

  log.error("Execution of method '%s' failed: Unknown (%o)", method, value);
  return RpcError.internal;
}

const log = LogManager.createLog("rpc.localHub");

/**
 * Provides methods for registering and calling local RPC methods.
 */
export class LocalRpcHub<TRpcContext extends IRpcContext> implements ILocalRpcHub<TRpcContext> {
  private readonly callbacks: Dictionary<RpcCallback<TRpcContext>> = {};

  has(method: string): boolean
  {
    return method in this.callbacks;
  }

  invoke<T, E>(context: TRpcContext, method: string, args: any[], timeout: Optional<number>): RpcResult<T, E>
  {
    const callback = this.callbacks[method];
    if (!callback)
      return Promise.resolve(Err(RpcError.createMethodNotFound(method)));

    let result: any;
    try
    {
      result = callback.apply(undefined, [context, ...args]);
    }
    catch (ex)
    {
      return Promise.resolve(Err(createRpcCallError(method, ex)));
    }

    return new Promise<Result<any, RpcError<any>>>((resolve) =>
    {
      let tid: Optional<number> = undefined;
      if (timeout !== undefined)
      {
        tid = setTimeout(() =>
        {
          resolve(Err(RpcError.timeout));
        }, timeout);
      }

      Promise.resolve(result).then(result =>
      {
        if (tid)
        {
          clearTimeout(tid);
          tid = undefined;
        }

        resolve(Ok(result));
      }, reason =>
        {
          if (tid)
          {
            clearTimeout(tid);
            tid = undefined;
          }

          if (!(reason instanceof RpcError))
            log.error("Execution of method '%s' failed promise: %o", method, reason);

          resolve(Err(createRpcCallError(method, reason)));
        });
    });
  }

  notify(context: TRpcContext, method: string, args: any[]): void
  {
    const callback = this.callbacks[method];
    if (!callback)
      return;

    try
    {
      callback.apply(undefined, [context, ...args]);
    }
    catch (ex)
    {
    }
  }

  off(method: string)
  {
    delete this.callbacks[method];
  }

  on(method: string, callback: RpcCallback<TRpcContext>)
  {
    this.callbacks[method] = callback;
  }
}